import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Helper function to parse permissions
const parsePermissions = (permissions) => {
  if (!permissions) return [];
  if (Array.isArray(permissions)) return permissions;
  if (typeof permissions === 'string') {
    return permissions.split(',').map(p => p.trim()).filter(p => p);
  }
  return [];
};

// Helper function to check if user has admin portal access
const canAccessAdminPortal = (user) => {
  if (!user) return false;
  
  // Admin type users always have access
  if (user.type === 'admin' || user.source === 'admin') return true;
  
  const permissions = parsePermissions(user.permissions);
  
  // Check for admin portal access permission or wildcard
  if (permissions.includes('ACCESS_ADMIN_PORTAL') || 
      permissions.includes('ALL') || 
      permissions.includes('*') ||
      permissions.includes('ALL_PERMISSIONS')) {
    return true;
  }
  
  // wscewbcyewdyugewyuewiud
  // Check role codes that have admin portal access
  const roleCode = user.primaryRole?.code || user.role;
  const allowedRoles = ['ADMIN', 'SUPER_ADMIN', 'CONTENT_MANAGER', 'EDITOR', 'CONTENT_WRITER'];
  
  if (allowedRoles.includes(roleCode)) return true;
  
  // Check roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(role => allowedRoles.includes(role.code));
  }
  
  return false;
};

// Helper function to check if user has required permissions
const hasRequiredPermissions = (user, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (!user) return false;
  
  const userPermissions = parsePermissions(user.permissions);
  
  // Check for wildcard/admin permissions
  if (userPermissions.includes('ALL') || 
      userPermissions.includes('*') ||
      userPermissions.includes('ALL_PERMISSIONS') ||
      user.type === 'admin' ||
      user.source === 'admin') {
    return true;
  }
  
  // Check if user has at least one of the required permissions
  return requiredPermissions.some(perm => userPermissions.includes(perm));
};

const ProtectedRoute = ({ children, requiredPermissions = [], requiredRoles = [] }) => {
  // Use a stable selector to prevent unnecessary re-renders
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  
  // Check if token exists in localStorage (handles token expiry redirect)
  const token = localStorage.getItem('token');

  // Memoize permission checks to prevent unnecessary re-renders
  const canAccess = useMemo(() => canAccessAdminPortal(user), [user]);
  const hasPermissions = useMemo(() => hasRequiredPermissions(user, requiredPermissions), [user, requiredPermissions]);

  // Not authenticated or token missing - redirect to login
  if (!isAuthenticated || !user || !token) {
    // Clear any stale auth data
    if (!token) {
      localStorage.removeItem('user');
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user can access admin portal
  if (!canAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check required roles if specified
  if (requiredRoles.length > 0) {
    const userRole = user.primaryRole?.code || user.role;
    const userRoles = user.roles?.map(r => r.code) || [];
    
    // Admin users bypass role checks
    if (user.type !== 'admin' && user.source !== 'admin') {
      const hasRole = requiredRoles.includes(userRole) || 
                      requiredRoles.some(r => userRoles.includes(r));
      
      if (!hasRole) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // Check required permissions if specified
  if (requiredPermissions.length > 0) {
    if (!hasPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
